<template>
  <ValidationObserver ref='validator' slim v-slot='{ handleSubmit }'>
    <div class='row'>
      <div class='col-xs-12 col-sm-12 col-md-6 col-lg-6'>
        <zipCode-locality-selector required :label="$t('identification.smartMeter.zipCodeOrLocality')" v-model='subCity'
          class='zipcode-margin' :inputClass="'lib__field__input'"
          :placeHolder="$t('new-connection-cmh.zipCodeOrLocality')"
          :unorderedListContainerClass="'autocomplete-suggestions'"
          :suggestionItemClass="'autocomplete-suggestion-item'"
          :suggestionItemTextClass="'autocomplete-suggestion-text'" />
      </div>
      <div class='col-xs-12 col-sm-12 col-md-6'>
        <text-box :label="$t('identification.smartMeter.eanCode')"
          :rules="{ 'ean-code-validator': true, required: true }" placeholder='541447856985698523'
          v-model='eanCode'></text-box>
        <div class='lib__field__note'>
          <a href='#' @click='whereFoundEanModalOpen = true' v-if='!isIdentifiedAndCanContinue'>{{
            $t('ean.whereFoundEan.title') }}</a>
        </div>
      </div>
    </div>
    <template v-if='!isIdentifiedAndCanContinue'>
      <ModalDialog :closeable="$t('common.close')" :open.sync='whereFoundEanModalOpen' size='small'>
        <h3 v-html="$t('ean.whereFoundEan.title')"></h3>
        <p>
          <span v-html="$t('ean.whereFoundEan.invoice')"></span>
          <br>
          <span v-html="$t('ean.whereFoundEan.formOnline1')"></span>
          <button class='lib__chat_trigger lib__btn lib__btn--primary lib__btn--small' @click='openChat'><i
              class='fas fa-comments'></i></button>
          <br>
          <span v-html="$t('ean.whereFoundEan.formOnline2')"></span>
        </p>
      </ModalDialog>

      <connection-point-area :check-for-elec='true' :check-for-gas='false' :hide-success-messages='false'
        :showSynergridLink='true' :sub-city='subCity' @canAllowElec='identificationDisabled = !$event' />

      <template v-if='!isIdentified && !identificationDisabled'>
        <Recaptcha :site-key='siteKey' :by-pass='$testMode' v-model='recaptchaToken' :key='recaptchaKey' />

        <nav class='lib__btns-nav-list' >
          <button @click='handleSubmit(identify)' class='lib__btn lib__btn--rounded lib__btn--primary' type='button'
            :class="{ 'lib__btn--loading': loading, 'lib__btn--disabled': !(subCity && subCity.gridManagers && subCity.gridManagers.electricityManager && subCity.gridManagers.electricityManager.isOres) || loading }">
            {{ $t('identification.smartMeter.identify') }}
          </button>
        </nav>
      </template>
    </template>

  </ValidationObserver>

</template>

<script lang='ts'>
import CollapseInfo from '@/components/display/CollapseInfo.vue'
import ConnectionPointArea from '@/components/inputs/Address/ConnectionPointArea.vue'
import ZipCodeLocalitySelector from '@/components/inputs/Address/ZipCodeLocalitySelector.vue'
import {
  CheckEanForSmartMeterResponse,
  CheckEanSmartMeterResult,
  EanConnectionContractResult,
  GetConnectionContractRequest,
  MetersError,
  SubCity
} from '@/models'
import { chatService, helper } from '@/services'
import { AxiosResponse } from 'axios'
import Component from 'vue-class-component'
import { Emit, Vue, Watch } from 'vue-property-decorator'
import { Mutation, State } from 'vuex-class'

@Component({
  name: 'identification',
  components: {
    ZipCodeLocalitySelector,
    ConnectionPointArea,
    CollapseInfo
  }
})
export default class Identification extends Vue {
  //#region [Data]
  @State('subCity', { namespace: 'smartMeterConnection/identification' })
  public subCityState!: SubCity | null
  @State('eanCode', { namespace: 'smartMeterConnection/identification' })
  public eanCodeState!: string | null
  @State('requestId', { namespace: 'smartMeterConnection' })
  public requestId!: string
  @State('checkEanResult', { namespace: 'smartMeterConnection/identification' })
  public eanResultState!: CheckEanSmartMeterResult
  @State('connectionContract', { namespace: 'smartMeterConnection/identification' })
  public connectionContractState!: EanConnectionContractResult | null

  public subCity: SubCity | null = null
  public eanCode: string | null = null

  public recaptchaToken: string | null = null

  public identificationDisabled: boolean = false
  public connectionContract: EanConnectionContractResult | null = null
  public eanResult: CheckEanSmartMeterResult | null = null
  public loading: boolean = false
  public isIdentified: boolean = false
  public recaptchaKey: number = 0

  public whereFoundEanModalOpen: boolean = false
  //#endregion

  //#region [Computed]
  public get isIdentifiedAndCanContinue(): boolean {
    return this.isIdentified && !!this.eanResult && this.eanResult.error === MetersError.NoError
  }
  public get siteKey() {
    return process.env.VUE_APP_CAPTCHA_SITE_KEY
  }
  //#endregion

  //#region [Mutation]
  @Mutation('STORE', { namespace: 'smartMeterConnection/identification' })
  public store!: (index: { subCity: SubCity | null; eanCode: string | null }) => void

  @Mutation('STORE_EAN_RESULT', { namespace: 'smartMeterConnection/identification' })
  public storeEanResult!: (index: CheckEanSmartMeterResult | null) => void

  @Mutation('STORE_CONNECTION_CONTRACT', { namespace: 'smartMeterConnection/identification' })
  public storeConnectionContract!: (index: EanConnectionContractResult | null) => void

  @Mutation('CLEAR_API_ERRORS', { namespace: 'apiErrors' })
  private clearApiErrors!: () => void
  //#endregion

  //#region [Watch]
  @Watch('eanCode')
  @Emit('eanCode')
  public onEanCodeChange(value: string | null) {
    this.clearApiErrors()
    if (value && value === this.eanCodeState) {
      // User input is the same as last identified EAN
      this.isIdentified = true
      return value
    }
    this.isIdentified = false
    return value
  }

  @Watch('eanCodeState')
  public onEanCodeStateChange(value: string | null) {
    if (!value) {
      this.isIdentified = false
    }
  }

  @Watch('isIdentified')
  @Emit('isIdentified')
  public onIsIdentifiedChange(value: boolean): boolean | null {
    return value
  }

  @Watch('isIdentifiedAndCanContinue', { immediate: true })
  @Emit('canContinue')
  public isIdentifiedAndCanContinueChange(value: boolean): boolean | null {
    return value
  }

  @Watch('eanResult')
  @Emit('eanResult')
  public onEanResultChange(value: CheckEanSmartMeterResult): CheckEanSmartMeterResult | null {
    return value
  }

  @Watch('connectionContract')
  @Emit('connectionContract')
  public onConnectionContractChange(value: EanConnectionContractResult): EanConnectionContractResult | null {
    return value
  }

  //#endregion

  //#region [Method]
  public async mounted() {
    this.subCity = helper.clone(this.subCityState)
    this.eanCode = helper.clone(this.eanCodeState)
    this.eanResult = helper.clone(this.eanResultState)
    this.connectionContract = helper.clone(this.connectionContractState)
    this.isIdentified = !!this.eanResultState

    if (this.$route.query.zipCode) {
      const zipCode = this.$route.query.zipCode
      const subCities = await this.getSubcities(Array.isArray(zipCode) ? zipCode[0] : zipCode)
      if (subCities.length > 0) {
        this.subCity = subCities[0]
      }
    }
    if (this.$route.query.ean) {
      const ean = this.$route.query.ean
      this.eanCode = Array.isArray(ean) ? ean[0] : ean
    }
  }

  private async getSubcities(zipCode: string | null): Promise<SubCity[]> {
    let url = 'subcities'
    if (zipCode !== undefined) {
      url += `?zipcode=${zipCode}`
    }

    const subCities = await this.$addressRepository
      .get<SubCity[]>(url, {
        cache: {
          cachePredicate: (status) => true,
        }
      })
      .then((response) => response.data)

    return subCities.filter((sc) => !sc.isFictive)
  }

  public async checkEanForSmartMeter(): Promise<CheckEanForSmartMeterResponse> {
    if (!this.recaptchaToken) {
      throw new Error('No recaptcha token found')
    }

    const response = await this.$api.get<CheckEanForSmartMeterResponse>(
      `api/assets/check-ean/${this.eanCode?.trim()}/smart-meter`,
      {
        headers: {
          'g-recaptcha-response': this.recaptchaToken
        },
        cache: {
          cachePredicate: (status) => true,
        }
      }
    )
    return response.data
  }

  public async getConnectionContract(token: string | null): Promise<EanConnectionContractResult | null> {
    if (!token) {
      throw new Error('No token found in sessionStorage');
    }

    const response = await this.$api.get<EanConnectionContractResult>(
      `api/consumptionMeasurements/connection-contract/${this.eanCode!.trim()}`,
      {
        headers: {
          Authorization: token
        },
        cache: {
          cachePredicate: (status) => true,
        }
      }
    )
    if (response.status >= 200 && response.status < 300) {
      return response.data
    }
    // Error loading connection contract
    return null
  }

  public async identify() {
    if (this.eanCode && this.subCity?.gridManagers.electricityManager?.isOres) {
      try {
        this.loading = true

        const checkEanForSmartMeterResponse = await this.checkEanForSmartMeter()

        this.eanResult = checkEanForSmartMeterResponse.eanInfo
        this.storeEanResult(this.eanResult)

        if (this.eanResult.error !== MetersError.NotFound) {
          if (checkEanForSmartMeterResponse.token) {
            sessionStorage.setItem('token', checkEanForSmartMeterResponse.token)

            const token = sessionStorage.getItem('token')
            this.connectionContract = await this.getConnectionContract(token)

            this.storeConnectionContract(this.connectionContract)
          }

          this.store({ subCity: this.subCity, eanCode: this.eanCode!.trim() })
        }

        this.loading = false
        this.isIdentified = true

        if (!this.isIdentifiedAndCanContinue) {
          // Refresh the captcha for next identification to work
          this.recaptchaKey += 1
        }
      } catch (err) {
        this.loading = false
        this.isIdentified = false
        this.storeEanResult(null)
        this.storeConnectionContract(null)
      }
    }
  }

  private openChat() {
    chatService.openChat()
  }

  //#endregion

  //#region [Emit]
  //#endregion
}
</script>
